import React, { useEffect, useState } from 'react';
import {
    FlatList,
    VStack,
    HStack,
} from 'native-base';
import {
    Effects,
    MenuFilter,
    PotencyUnit,
    StrainType
} from '@springbig/shared/dist/services/dutchie/schema-types';
import {
    Brand,
    defaultCategories,
    useBrandsByRetailerIdQuery,
    useDutchieStore,
} from '@springbig/shared';
import { ProductListProps } from './ProductList';
import { SBDropdown } from '~/shared/SBDropdown';
import { SBRangeSlider } from '~/shared/SBRangeSlider';
import { Caption } from '~/shared/Typography';
import { Button, CheckBox, } from '~/shared/Theme';

export type FilterModalProp = {
    setShowFilters: () => void;
    filters: MenuFilter | null | undefined;
    setFilters: (filters: MenuFilter) => Promise<void>;
};

export const FilterView = ({ setShowFilters, filters, setFilters }: FilterModalProp) => {

    const [selectedStrainType, setSelectedStrainType] =
        useState<StrainType | undefined>(undefined);

    const [selectedEffects, setSelectedEffects] =
        useState<Array<Effects>>([]);

    const [priceMinValue, setPriceMinValue] =
        useState(filters?.minPrice ?? 0);

    const [priceMaxValue, setPriceMaxValue] =
        useState(filters?.maxPrice ?? 1000);

    const [thcMinValue, setThcMinValue] =
        useState(filters?.potencyThc?.min ?? 0);

    const [thcMaxValue, setThcMaxValue] =
        useState(filters?.potencyThc?.max ?? 100);

    const [cbdMinValue, setCbdMinValue] =
        useState(filters?.potencyCbd?.min ?? 0);

    const [cbdMaxValue, setCbdMaxValue] =
        useState(filters?.potencyThc?.max ?? 100);

    const [brands, setBrands] =
        useState<Brand[]>([]);

    const {
        setMenuCategories,
        retailer,
        menuType,
        brandFilter,
        setBrandFilter,
        categoryFilter,
        setCategoryFilter
    } = useDutchieStore();

    const {
        mutate: getBrands,
        data,
        isLoading,
    } = useBrandsByRetailerIdQuery();

    useEffect(() => {

        if (retailer && menuType && !data && !isLoading) {
            const variables = {
                'retailerId': retailer.id,
                menuType,
            };
            getBrands(variables, {
                onSuccess(_data) {

                    if (_data?.menu.brands) {
                        setBrands(_data?.menu.brands);
                    }
                },
            });
        }
    }, [retailer, menuType, getBrands]);

    useEffect(() => {

        if (filters) {
            if (filters.strainType) {
                setSelectedStrainType(filters.strainType);
            }

            if (filters.effects) {
                setSelectedEffects(filters.effects);
            }
        }
    }, []);

    const onSelectFilter = (): void => {

        setShowFilters();
        const newFilters : MenuFilter = {
            strainType: selectedStrainType,
            effects: selectedEffects,
            minPrice: priceMinValue,
            maxPrice: priceMaxValue,
            potencyCbd: { max: cbdMaxValue, min: cbdMinValue, unit: PotencyUnit.Percentage },
            potencyThc: { max: thcMaxValue, min: thcMinValue, unit: PotencyUnit.Percentage },
            brandIds: brandFilter,
            categories: categoryFilter
        };
        setFilters(newFilters);
    };

    const clearFilters = (): void => {

        setShowFilters();
        setMenuCategories(defaultCategories);
        setFilters(null);
        setBrandFilter(undefined);
    };

    const onSelectEffect = (effects: Effects): void => {

        if (selectedEffects.includes(effects)) {
            setSelectedEffects((prevState) => prevState.filter((e) => e !== effects));
        }
        else {
            setSelectedEffects((prevState) => [effects, ...prevState]);
        }
    };

    const onSelectStrainType = (strainType: StrainType): void => {

        if (strainType === selectedStrainType) {
            setSelectedStrainType(undefined);
        }
        else {
            setSelectedStrainType(strainType);
        }
    };

    const onChangeBrand = (item: Brand): void => {

        setBrandFilter((prev) => {

            if (prev?.includes(item.id)) {
                return prev?.filter((id) => id !== item.id);
            }
            else if (prev) {
                return [item.id, ...prev];
            }

            return [item.id];
        });
    };

    const onChangeCategory = (item: ProductListProps) => {

        setCategoryFilter((prev) => {

            if (prev?.includes(item.category)) {
                return prev?.filter((category) => category !== item.category);
            }
            else if (prev) {
                return [item.category, ...prev];
            }

            return [item.category];
        });
    };

    return (
        <VStack
            justifyContent={'space-between'}
            overflowY={'auto'}
            w={'100%'}
        >
            <VStack space={4} pb={4} px={2}>
                <SBDropdown title={'brand'}>
                    <FlatList
                        mr={-2}
                        h={'85 px'}
                        data={brands}
                        renderItem={({ item }: { item: Brand }) =>

                            <CheckBox
                                isChecked={brandFilter?.includes(item.id)}
                                value={item.id}
                                onChange={() => onChangeBrand(item)}
                            >
                                <Caption textAlign={'left'}>
                                    { item.name }
                                </Caption>
                            </CheckBox>
                        }
                    />
                </SBDropdown>
                <SBDropdown title={'category'}>
                    <FlatList
                        mr={-2}
                        h={'85 px'}
                        data={defaultCategories.filter(cat => cat.category)}
                        renderItem={({ item }: { item: ProductListProps }) =>

                            <CheckBox
                                isChecked={categoryFilter?.includes(item.category)}
                                value={item.title}
                                onChange={() => onChangeCategory(item)}
                            >
                                <Caption textAlign={'left'}>
                                    { item.title }
                                </Caption>
                            </CheckBox>
                        }
                    />
                </SBDropdown>
                <SBDropdown title={'effects'}>
                    <FlatList
                        mr={-2}
                        h={'85 px'}
                        data={Object.values(Effects)}
                        renderItem={({ item }: { item: Effects }) =>

                            <CheckBox
                                isChecked={selectedEffects.includes(item)}
                                value={item.toLowerCase().replace('_', ' ')}
                                onChange={() => onSelectEffect(item)}
                            >
                                <Caption textAlign={'left'}>
                                    { item.toLowerCase().replace('_', ' ') }
                                </Caption>
                            </CheckBox>
                        }
                    />
                </SBDropdown>
                <SBDropdown title={'strain type'}>
                    <FlatList
                        mr={-2}
                        data={[StrainType.Indica, StrainType.Sativa, StrainType.Hybrid]}
                        renderItem={({ item }: {item: StrainType}) =>

                            <CheckBox
                                isChecked={selectedStrainType === item}
                                value={item.toLowerCase().replace('_', ' ')}
                                onChange={() => onSelectStrainType(item)}
                            >
                                <Caption textAlign={'left'}>
                                    { item.toLowerCase() }
                                </Caption>
                            </CheckBox>
                        }
                    />
                </SBDropdown>
                <SBDropdown title={'potency'}>
                    <Caption>thc</Caption>
                    <SBRangeSlider
                        minValue={thcMinValue}
                        maxValue={thcMaxValue}
                        lowerBound={0}
                        upperBound={100}
                        onChangeMinValue={(value) => setThcMinValue(value)}
                        onChangeMaxValue={(value) => setThcMaxValue(value)}
                        type={'percentage'}
                    />
                    <Caption>cbd</Caption>
                    <SBRangeSlider
                        minValue={cbdMinValue}
                        maxValue={cbdMaxValue}
                        lowerBound={0}
                        upperBound={100}
                        onChangeMinValue={(value) => setCbdMinValue(value)}
                        onChangeMaxValue={(value) => setCbdMaxValue(value)}
                        type={'percentage'}
                    />
                </SBDropdown>
                <SBDropdown
                    title="price"
                    defaultShowChildren={true}
                >
                    <SBRangeSlider
                        minValue={priceMinValue}
                        maxValue={priceMaxValue}
                        lowerBound={0}
                        upperBound={1000}
                        onChangeMinValue={(value) => setPriceMinValue(value)}
                        onChangeMaxValue={(value) => setPriceMaxValue(value)}
                    />
                </SBDropdown>
            </VStack>
            <HStack
                justifyContent={'space-between'}
                alignItems={'center'}
                px={4}
            >
                <Button
                    text={'clear all'}
                    variant={'text'}
                    onPress={clearFilters}
                />
                <Button
                    text={'shop.apply'}
                    onPress={onSelectFilter}
                />
            </HStack>
        </VStack>
    );
};
